/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import RootLayout from './layouts/RootLayout';
import ProtectedRoute from './components/ProtectedRoute';
import { useAnalytics } from './analytics';

// Eagerly load the Home page as it contains the LCP element for the main landing page
import Home from './pages/home/Home';

// Lazy load core pages
const SearchPage = lazy(() => import('./pages/search/Search'));
const BranchDetail = lazy(() => import('./pages/ifsc/BranchDetail'));
const IFSCPage = lazy(() => import('./pages/ifsc/IFSCPage'));
const BankDetail = lazy(() => import('./pages/bank/BankDetail'));
const BankPage = lazy(() => import('./pages/bank/BankPage'));
const BankStateDetail = lazy(() => import('./pages/bank/BankStateDetail'));
const StatePage = lazy(() => import('./pages/bank/StatePage'));
const StateDetail = lazy(() => import('./pages/bank/StateDetail'));
const CityPage = lazy(() => import('./pages/city/CityPage'));
const CityDetail = lazy(() => import('./pages/city/CityDetail'));

// Lazy load calculators
const EmiCalculator = lazy(() => import('./pages/calculators/EmiCalculator'));
const SipCalculator = lazy(() => import('./pages/calculators/SipCalculator'));
const FdCalculator = lazy(() => import('./pages/calculators/FdCalculator'));
const GstCalculator = lazy(() => import('./pages/calculators/GstCalculator'));

const CalculatorsHub = lazy(() => import('./pages/calculators/CalculatorsHub'));
const PpfCalculator = lazy(() => import('./pages/calculators/PpfCalculator'));
const RdCalculator = lazy(() => import('./pages/calculators/RdCalculator'));
const HomeLoanCalculator = lazy(() => import('./pages/calculators/HomeLoanCalculator'));
const PersonalLoanCalculator = lazy(() => import('./pages/calculators/PersonalLoanCalculator'));
const CarLoanCalculator = lazy(() => import('./pages/calculators/CarLoanCalculator'));
const EducationLoanCalculator = lazy(() => import('./pages/calculators/EducationLoanCalculator'));
const CompoundInterestCalculator = lazy(() => import('./pages/calculators/CompoundInterestCalculator'));
const SimpleInterestCalculator = lazy(() => import('./pages/calculators/SimpleInterestCalculator'));
const EpfCalculator = lazy(() => import('./pages/calculators/EpfCalculator'));
const NpsCalculator = lazy(() => import('./pages/calculators/NpsCalculator'));
const SsyCalculator = lazy(() => import('./pages/calculators/SsyCalculator'));
const ScssCalculator = lazy(() => import('./pages/calculators/ScssCalculator'));
const InflationCalculator = lazy(() => import('./pages/calculators/InflationCalculator'));

// Lazy load Legal & Trust pages
const AboutUs = lazy(() => import('./pages/legal/AboutUs'));
const ContactUs = lazy(() => import('./pages/legal/ContactUs'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/legal/TermsAndConditions'));
const Disclaimer = lazy(() => import('./pages/legal/Disclaimer'));
const EditorialVerificationPolicy = lazy(() => import('./pages/legal/EditorialVerificationPolicy'));

// Phase 5 Comparison Architecture Foundation Pages
const CompareHub = lazy(() => import('./pages/compare/CompareHub'));
const SavingsFd = lazy(() => import('./pages/compare/SavingsFd'));
const NeftRtgsImpsUpi = lazy(() => import('./pages/compare/NeftRtgsImpsUpi'));
const LoanCreditCard = lazy(() => import('./pages/compare/LoanCreditCard'));
const FdRd = lazy(() => import('./pages/compare/FdRd'));
const HomeLoanPersonalLoan = lazy(() => import('./pages/compare/HomeLoanPersonalLoan'));
const PpfFd = lazy(() => import('./pages/compare/PpfFd'));
const RdFd = lazy(() => import('./pages/compare/RdFd'));
const EpfNps = lazy(() => import('./pages/compare/EpfNps'));
const ScssFd = lazy(() => import('./pages/compare/ScssFd'));
const HomeLoanLoanAgainstProperty = lazy(() => import('./pages/compare/HomeLoanLoanAgainstProperty'));
const CarLoanPersonalLoan = lazy(() => import('./pages/compare/CarLoanPersonalLoan'));
const GoldLoanPersonalLoan = lazy(() => import('./pages/compare/GoldLoanPersonalLoan'));
const FixedFloatingInterestRate = lazy(() => import('./pages/compare/FixedFloatingInterestRate'));
const PrepaymentForeclosure = lazy(() => import('./pages/compare/PrepaymentForeclosure'));
const SecuredVsUnsecuredLoan = lazy(() => import('./pages/compare/SecuredVsUnsecuredLoan'));
const RewardPointsVsCashback = lazy(() => import('./pages/compare/RewardPointsVsCashback'));
const FullPaymentVsMinimumDue = lazy(() => import('./pages/compare/FullPaymentVsMinimumDue'));
const CreditCardVsDebitCard = lazy(() => import('./pages/compare/CreditCardVsDebitCard'));

// Lazy load Banking Guides Group
const GuidesHub = lazy(() => import('./pages/guides/GuidesHub'));
const BankingHub = lazy(() => import('./pages/guides/BankingHub'));
const SavingsHub = lazy(() => import('./pages/guides/SavingsHub'));
const LoansHub = lazy(() => import('./pages/guides/LoansHub'));
const CreditCardsHub = lazy(() => import('./pages/guides/CreditCardsHub'));
const IfscCodeGuide = lazy(() => import('./pages/guides/IfscCodeGuide'));
const NeftRtgsImpsGuide = lazy(() => import('./pages/guides/NeftRtgsImpsGuide'));
const UpiTransfersGuide = lazy(() => import('./pages/guides/UpiTransfersGuide'));
const BankingSafetyGuide = lazy(() => import('./pages/guides/BankingSafetyGuide'));
const BankStatementsGuide = lazy(() => import('./pages/guides/BankStatementsGuide'));
const BestSavingsAccountsGuide = lazy(() => import('./pages/guides/BestSavingsAccountsGuide'));
const FixedDepositGuide = lazy(() => import('./pages/guides/FixedDepositGuide'));
const CurrentAccountGuide = lazy(() => import('./pages/guides/CurrentAccountGuide'));
const PersonalLoanGuide = lazy(() => import('./pages/guides/PersonalLoanGuide'));
const HomeLoanGuide = lazy(() => import('./pages/guides/HomeLoanGuide'));
const CreditCardGuide = lazy(() => import('./pages/guides/CreditCardGuide'));
const CreditCardBillingCycleGuide = lazy(() => import('./pages/guides/CreditCardBillingCycleGuide'));
const MinimumDueOnCreditCard = lazy(() => import('./pages/guides/MinimumDueOnCreditCard'));
const CreditCardInterestCharges = lazy(() => import('./pages/guides/CreditCardInterestCharges'));
const CreditScoreBasics = lazy(() => import('./pages/guides/CreditScoreBasics'));
const CreditUtilizationGuide = lazy(() => import('./pages/guides/CreditUtilizationGuide'));
const SecuredVsUnsecuredCredit = lazy(() => import('./pages/guides/SecuredVsUnsecuredCredit'));
const RewardPointsAndCashbackGuide = lazy(() => import('./pages/guides/RewardPointsAndCashbackGuide'));
const CreditCardSafetyGuide = lazy(() => import('./pages/guides/CreditCardSafetyGuide'));
const ChequeGuide = lazy(() => import('./pages/guides/ChequeGuide'));
const CancelledChequeGuide = lazy(() => import('./pages/guides/CancelledChequeGuide'));
const PassbookGuide = lazy(() => import('./pages/guides/PassbookGuide'));
const TypesOfBankAccountsGuide = lazy(() => import('./pages/guides/TypesOfBankAccountsGuide'));
const AePsGuide = lazy(() => import('./pages/guides/AePsGuide'));
const RecurringDepositGuide = lazy(() => import('./pages/guides/RecurringDepositGuide'));
const PpfGuide = lazy(() => import('./pages/guides/PpfGuide'));
const EpfGuide = lazy(() => import('./pages/guides/EpfGuide'));
const NpsGuide = lazy(() => import('./pages/guides/NpsGuide'));
const ScssGuide = lazy(() => import('./pages/guides/ScssGuide'));
const TaxSavingInvestmentsGuide = lazy(() => import('./pages/guides/TaxSavingInvestmentsGuide'));
const EducationLoanGuide = lazy(() => import('./pages/guides/EducationLoanGuide'));
const CarLoanGuide = lazy(() => import('./pages/guides/CarLoanGuide'));
const GoldLoanGuide = lazy(() => import('./pages/guides/GoldLoanGuide'));
const LoanEligibilityGuide = lazy(() => import('./pages/guides/LoanEligibilityGuide'));
const LoanDocumentsGuide = lazy(() => import('./pages/guides/LoanDocumentsGuide'));
const LoanForeclosurePrepaymentGuide = lazy(() => import('./pages/guides/LoanForeclosurePrepaymentGuide'));
const UnderstandingLoanEmi = lazy(() => import('./pages/guides/UnderstandingLoanEmi'));
const RbiRetailDirectGuide = lazy(() => import('./pages/guides/RbiRetailDirectGuide'));
const NpsTier1VsTier2Guide = lazy(() => import('./pages/guides/NpsTier1VsTier2Guide'));
const MutualFundBasicsGuide = lazy(() => import('./pages/guides/MutualFundBasicsGuide'));
const LumpsumCalculator = lazy(() => import('./pages/calculators/LumpsumCalculator'));
const GoalPlannerCalculator = lazy(() => import('./pages/calculators/GoalPlannerCalculator'));

// Lazy load admin panels
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const ImportExcel = lazy(() => import('./pages/admin/ImportExcel'));
const ImportHistory = lazy(() => import('./pages/admin/ImportHistory'));
const NotFound = lazy(() => import('./pages/NotFound'));

function AnalyticsTracker() {
  useAnalytics();
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AnalyticsTracker />
      <Suspense fallback={null}>
        <Routes>
          <Route element={<RootLayout />}>
            {/* Public Core Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/ifsc" element={<IFSCPage />} />
            <Route path="/ifsc/:ifsc" element={<BranchDetail />} />
            <Route path="/bank" element={<BankPage />} />
            <Route path="/bank/:bankSlug" element={<BankDetail />} />
            <Route path="/bank/:bankSlug/:state" element={<BankStateDetail />} />
            <Route path="/state" element={<StatePage />} />
            <Route path="/state/:stateSlug" element={<StateDetail />} />
            <Route path="/city" element={<CityPage />} />
            <Route path="/city/:citySlug" element={<CityDetail />} />
            
            {/* Standardized Calculators Paths */}
            <Route path="/calculators/emi" element={<EmiCalculator />} />
            <Route path="/calculators/sip" element={<SipCalculator />} />
            <Route path="/calculators/fd" element={<FdCalculator />} />
            <Route path="/calculators/gst" element={<GstCalculator />} />

            {/* Backwards Compatibility / Legacy Paths */}
            <Route path="/calculator/emi" element={<Navigate to="/calculators/emi" replace />} />
            <Route path="/calculator/sip" element={<Navigate to="/calculators/sip" replace />} />
            <Route path="/calculator/fd" element={<Navigate to="/calculators/fd" replace />} />
            <Route path="/calculator/gst" element={<Navigate to="/calculators/gst" replace />} />

            <Route path="/calculators" element={<CalculatorsHub />} />
            <Route path="/calculators/ppf-calculator" element={<PpfCalculator />} />
            <Route path="/calculators/rd-calculator" element={<RdCalculator />} />
            <Route path="/calculators/home-loan-calculator" element={<HomeLoanCalculator />} />
            <Route path="/calculators/personal-loan-calculator" element={<PersonalLoanCalculator />} />
            <Route path="/calculators/car-loan-calculator" element={<CarLoanCalculator />} />
            <Route path="/calculators/education-loan-calculator" element={<EducationLoanCalculator />} />
            <Route path="/calculators/compound-interest-calculator" element={<CompoundInterestCalculator />} />
            <Route path="/calculators/simple-interest-calculator" element={<SimpleInterestCalculator />} />
            <Route path="/calculators/epf-calculator" element={<EpfCalculator />} />
            <Route path="/calculators/nps-calculator" element={<NpsCalculator />} />
            <Route path="/calculators/ssy-calculator" element={<SsyCalculator />} />
            <Route path="/calculators/scss-calculator" element={<ScssCalculator />} />
            <Route path="/calculators/lumpsum" element={<LumpsumCalculator />} />
            <Route path="/calculators/goal-planner" element={<GoalPlannerCalculator />} />
            <Route path="/calculators/inflation-calculator" element={<InflationCalculator />} />

            {/* Legal Pages Group */}
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/disclaimer" element={<Disclaimer />} />

            {/* Consolidated Trust & Policy Resources */}
            <Route path="/editorial-verification-policy" element={<EditorialVerificationPolicy />} />

            {/* Permanent Redirects for Retired Pages to Preserve SEO Equity */}
            <Route path="/why-trust-finsahaj" element={<Navigate to="/about-us" replace />} />
            <Route path="/why-trust-finsahaj-india" element={<Navigate to="/about-us" replace />} />
            <Route path="/data-and-verification-policy" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/accuracy-and-corrections-policy" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/editorial-policy" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/editorial-standards" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/review-process" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/data-source-policy" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/source-reference-policy" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/how-we-verify-banking-information" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/methodology" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/corrections-policy" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/content-update-policy" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/last-updated-policy" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/information-accuracy" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/report-an-error" element={<Navigate to="/editorial-verification-policy" replace />} />
            <Route path="/feedback-and-suggestions" element={<Navigate to="/editorial-verification-policy" replace />} />

            {/* Phase 5 Comparison Architecture Foundation Routes */}
            <Route path="/compare" element={<CompareHub />} />
            <Route path="/compare/savings-account-vs-fixed-deposit" element={<SavingsFd />} />
            <Route path="/compare/neft-vs-rtgs-vs-imps-vs-upi" element={<NeftRtgsImpsUpi />} />
            <Route path="/compare/personal-loan-vs-credit-card" element={<LoanCreditCard />} />
            <Route path="/compare/credit-card-vs-personal-loan" element={<Navigate to="/compare/personal-loan-vs-credit-card" replace />} />
            <Route path="/compare/fixed-deposit-vs-recurring-deposit" element={<FdRd />} />
            <Route path="/compare/home-loan-vs-personal-loan" element={<HomeLoanPersonalLoan />} />
            <Route path="/compare/ppf-vs-fixed-deposit" element={<PpfFd />} />
            <Route path="/compare/rd-vs-fixed-deposit" element={<RdFd />} />
            <Route path="/compare/epf-vs-nps" element={<EpfNps />} />
            <Route path="/compare/scss-vs-fixed-deposit" element={<ScssFd />} />
            <Route path="/compare/home-loan-vs-loan-against-property" element={<HomeLoanLoanAgainstProperty />} />
            <Route path="/compare/car-loan-vs-personal-loan" element={<CarLoanPersonalLoan />} />
            <Route path="/compare/gold-loan-vs-personal-loan" element={<GoldLoanPersonalLoan />} />
            <Route path="/compare/fixed-vs-floating-interest-rate" element={<FixedFloatingInterestRate />} />
            <Route path="/compare/prepayment-vs-foreclosure" element={<PrepaymentForeclosure />} />
            <Route path="/compare/secured-vs-unsecured-loan" element={<SecuredVsUnsecuredLoan />} />
            <Route path="/compare/reward-points-vs-cashback" element={<RewardPointsVsCashback />} />
            <Route path="/compare/full-payment-vs-minimum-due" element={<FullPaymentVsMinimumDue />} />
            <Route path="/compare/credit-card-vs-debit-card" element={<CreditCardVsDebitCard />} />

            {/* Banking Educational Guides (Phase 2C-A Authority Content) */}
            <Route path="/guides" element={<GuidesHub />} />
            <Route path="/guides/banking" element={<BankingHub />} />
            <Route path="/guides/savings" element={<SavingsHub />} />
            <Route path="/guides/loans" element={<LoansHub />} />
            <Route path="/guides/credit-cards" element={<CreditCardsHub />} />
            <Route path="/guides/what-is-ifsc-code" element={<IfscCodeGuide />} />
            <Route path="/guides/neft-vs-rtgs-vs-imps" element={<NeftRtgsImpsGuide />} />
            <Route path="/guides/how-upi-transfers-work" element={<UpiTransfersGuide />} />
            <Route path="/guides/banking-safety-tips" element={<BankingSafetyGuide />} />
            <Route path="/guides/understanding-bank-statements" element={<BankStatementsGuide />} />
            <Route path="/guides/best-savings-accounts-india" element={<BestSavingsAccountsGuide />} />
            <Route path="/guides/best-savings-accounts" element={<Navigate to="/guides/best-savings-accounts-india" replace />} />
            <Route path="/guides/fixed-deposit-guide" element={<FixedDepositGuide />} />
            <Route path="/guides/current-account-guide" element={<CurrentAccountGuide />} />
            <Route path="/guides/personal-loan-guide" element={<PersonalLoanGuide />} />
            <Route path="/guides/home-loan-guide" element={<HomeLoanGuide />} />
            <Route path="/guides/credit-card-guide" element={<CreditCardGuide />} />
            <Route path="/guides/credit-card-billing-cycle-guide" element={<CreditCardBillingCycleGuide />} />
            <Route path="/guides/minimum-due-on-credit-card" element={<MinimumDueOnCreditCard />} />
            <Route path="/guides/credit-card-interest-charges" element={<CreditCardInterestCharges />} />
            <Route path="/guides/credit-score-basics" element={<CreditScoreBasics />} />
            <Route path="/guides/credit-utilization-guide" element={<CreditUtilizationGuide />} />
            <Route path="/guides/secured-vs-unsecured-credit" element={<SecuredVsUnsecuredCredit />} />
            <Route path="/guides/reward-points-and-cashback-guide" element={<RewardPointsAndCashbackGuide />} />
            <Route path="/guides/credit-card-safety-guide" element={<CreditCardSafetyGuide />} />
            <Route path="/guides/cheque-guide" element={<ChequeGuide />} />
            <Route path="/guides/cancelled-cheque-guide" element={<CancelledChequeGuide />} />
            <Route path="/guides/passbook-guide" element={<PassbookGuide />} />
            <Route path="/guides/types-of-bank-accounts" element={<TypesOfBankAccountsGuide />} />
            <Route path="/guides/aeps-guide" element={<AePsGuide />} />
            <Route path="/guides/recurring-deposit-guide" element={<RecurringDepositGuide />} />
            <Route path="/guides/ppf-guide" element={<PpfGuide />} />
            <Route path="/guides/epf-guide" element={<EpfGuide />} />
            <Route path="/guides/nps-guide" element={<NpsGuide />} />
            <Route path="/guides/scss-guide" element={<ScssGuide />} />
            <Route path="/guides/mutual-fund-basics" element={<MutualFundBasicsGuide />} />
            <Route path="/guides/tax-saving-investments-guide" element={<TaxSavingInvestmentsGuide />} />
            <Route path="/guides/education-loan-guide" element={<EducationLoanGuide />} />
            <Route path="/guides/car-loan-guide" element={<CarLoanGuide />} />
            <Route path="/guides/gold-loan-guide" element={<GoldLoanGuide />} />
            <Route path="/guides/loan-eligibility-guide" element={<LoanEligibilityGuide />} />
            <Route path="/guides/loan-documents-guide" element={<LoanDocumentsGuide />} />
            <Route path="/guides/loan-foreclosure-and-prepayment-guide" element={<LoanForeclosurePrepaymentGuide />} />
            <Route path="/guides/understanding-loan-emi" element={<UnderstandingLoanEmi />} />
            <Route path="/guides/rbi-retail-direct-guide" element={<RbiRetailDirectGuide />} />
            <Route path="/guides/nps-tier-1-vs-tier-2" element={<NpsTier1VsTier2Guide />} />

            {/* Admin Restricted Panel Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/import"
              element={
                <ProtectedRoute>
                  <ImportExcel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/import-history"
              element={
                <ProtectedRoute>
                  <ImportHistory />
                </ProtectedRoute>
              }
            />

            <Route path="/404" element={<NotFound />} />

            {/* Wildcard Fallback */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
